import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Send,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import api from '../../services/api';
import { axelionColors } from '../../theme/axelionTheme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const ChatPage = () => {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [consultation, setConsultation] = useState(null);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load consultation info and messages
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [messagesRes, consultationRes] = await Promise.all([
          api.get(`/chat/${consultationId}/messages`),
          api.get(`/consultations/${consultationId}`),
        ]);
        setMessages(messagesRes.data || []);
        if (consultationRes?.data?.consultation) {
          setConsultation(consultationRes.data.consultation);
        }
      } catch (err) {
        console.error('Error loading chat:', err);
        toast.error('Ошибка загрузки чата');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [consultationId]);

  // Socket connection
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !consultationId) return;

    const socket = io(API_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      socket.emit('join-chat', { consultationId });
    });

    socket.on('message-received', (message) => {
      setMessages((prev) => {
        // Avoid duplicates
        if (prev.some((m) => m.id === message.id)) return prev;
        return [...prev, message];
      });
    });

    socket.on('user-typing', ({ userName }) => {
      setTypingUser(userName);
    });

    socket.on('user-stop-typing', () => {
      setTypingUser(null);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
    };
  }, [consultationId]);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    const text = newMessage.trim();
    if (!text || sending) return;

    setSending(true);
    setNewMessage('');

    // Clear typing indicator
    if (socketRef.current) {
      socketRef.current.emit('stop-typing', { consultationId });
    }

    try {
      if (socketRef.current?.connected) {
        socketRef.current.emit('send-message', { consultationId, text });
      } else {
        // REST fallback
        const res = await api.post(`/chat/${consultationId}/messages`, { text });
        setMessages((prev) => [...prev, res.data]);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Ошибка отправки');
      setNewMessage(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTyping = () => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing', { consultationId });
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current?.emit('stop-typing', { consultationId });
      }, 2000);
    }
  };

  const getPartnerName = () => {
    if (!consultation) return 'Собеседник';
    if (user?.role === 'client') {
      return consultation.lawyer?.name || 'Юрист';
    }
    return consultation.client?.name || 'Клиент';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: axelionColors.bgCream }}>
        <CircularProgress sx={{ color: axelionColors.gold }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: axelionColors.bgCream }}>
      {/* Header */}
      <Box
        sx={{
          background: axelionColors.bgLight,
          borderBottom: `1px solid ${axelionColors.borderLight}`,
          py: 2,
          px: 2,
          boxShadow: '0 2px 6px rgba(26, 26, 26, 0.06)',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                bgcolor: axelionColors.bgCream,
                color: axelionColors.textDark,
                border: `1px solid ${axelionColors.borderLight}`,
                '&:hover': { bgcolor: axelionColors.bgBeige },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ color: axelionColors.textDark }}>
                {getPartnerName()}
              </Typography>
              {typingUser && (
                <Typography variant="caption" sx={{ color: axelionColors.gold }}>
                  печатает...
                </Typography>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 2 }}>
        <Container maxWidth="md">
          {messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" sx={{ color: axelionColors.textMuted, mb: 1 }}>
                Начните диалог
              </Typography>
              <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                Напишите первое сообщение, чтобы начать консультацию
              </Typography>
            </Box>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.senderId === user?.id || msg.sender?.id === user?.id;
              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, maxWidth: '75%', flexDirection: isOwn ? 'row-reverse' : 'row' }}>
                    {!isOwn && (
                      <Avatar
                        sx={{ width: 32, height: 32, bgcolor: axelionColors.gold, fontSize: 14, mt: 0.5 }}
                      >
                        {(msg.sender?.name || '?').charAt(0)}
                      </Avatar>
                    )}
                    <Box
                      sx={{
                        bgcolor: isOwn ? axelionColors.gold : axelionColors.bgLight,
                        color: isOwn ? 'white' : axelionColors.textDark,
                        borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        px: 2,
                        py: 1,
                        border: isOwn ? 'none' : `1px solid ${axelionColors.borderLight}`,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      }}
                    >
                      <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {msg.text}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: 'right',
                          mt: 0.5,
                          opacity: 0.7,
                          fontSize: '0.65rem',
                        }}
                      >
                        {new Date(msg.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Container>
      </Box>

      {/* Input */}
      <Box
        sx={{
          background: axelionColors.bgLight,
          borderTop: `1px solid ${axelionColors.borderLight}`,
          py: 1.5,
          px: 2,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                handleTyping();
              }}
              onKeyDown={handleKeyDown}
              placeholder="Введите сообщение..."
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: axelionColors.bgCream,
                  '& fieldset': { borderColor: axelionColors.borderLight },
                  '&:hover fieldset': { borderColor: axelionColors.gold },
                  '&.Mui-focused fieldset': { borderColor: axelionColors.gold },
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              sx={{
                bgcolor: axelionColors.gold,
                color: 'white',
                width: 44,
                height: 44,
                '&:hover': { bgcolor: axelionColors.bronze },
                '&.Mui-disabled': { bgcolor: axelionColors.borderLight, color: axelionColors.textMuted },
              }}
            >
              {sending ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Send />}
            </IconButton>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ChatPage;
